import React, { useEffect, useState, useMemo } from 'react';
import { User } from 'types';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { CircularProgress, Box, Switch, FormControlLabel } from '@mui/material';
import { usePermissionCheck } from '../../../../../core/hooks/usePermissionCheck';
import { Button } from 'components/ui/button';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from '@mui/material';
import CreateUserSettings from './CreateUserSettings';
import ShowUserSetting from './ShowUserSettings/ShowUserSetting';
import styles from '../GeneralSettings.module.scss';
import EditUserSettings from './EditUserSettings/EditUserSetting';
import { useUserCrud } from 'core/hooks/useUserCrud';
import { isHiddenFromUserLists } from 'core/utils/filterVisibleWorkplaceUsers';
import useUserDataStore from 'store/useUserDataStore';
import useSettingsStore from 'store/useSettingsStore';
import { filterUserSettingsUsers } from './filterUserSettingsUsers';
import { CollapsibleUserSearch } from './CollapsibleUserSearch';
import { userSettingsUtils } from './utils';
import { getCurrentUserHierarchyId } from 'module/onboarding/utils/hierarchyPermissions';
import { useResetSettingsScroll } from '../../../utils/resetSettingsScroll';
import {
	isDesignatedOrganizationAdministrator,
	resolveUserSecurityRole,
} from '../OrganisationSettings/organizationAdministratorOptions';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import useUserData from 'store/useUserData';
import { canEditUserWithSecurityRole } from './securityRoleSelection';
import { resolveCurrentUserRole } from './hierarchySelection';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

export interface UserSettingsProps {
	handleFocusChange?: (focus: 'organization' | 'workplace' | 'user' | null) => void;
}

function UserSettings(props: UserSettingsProps) {
	const { handleFocusChange } = props;
	const { hasEditPermission, hasViewPermission, hasDeletePermission } = usePermissionCheck();
	const canViewUser = hasViewPermission('User');
	const canEditUser = hasEditPermission('User');
	const canDeleteUser = hasDeletePermission('User');
	const { showToast } = useToastContext();

	const { cleanHierarchyName, filterUsers } = userSettingsUtils;

	// Use the custom hook for user CRUD operations
	const {
		users,
		loading: usersLoading,
		error: usersError,
		fetchUsers,
		deleteUser: apiDeleteUser,
		clearError,
	} = useUserCrud();

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	useResetSettingsScroll(viewMode);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [userToDelete, setUserToDelete] = useState<User | null>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [showAboveWorkplaceUsers, setShowAboveWorkplaceUsers] = useState<boolean>(false);

	const workplaceId = getCurrentWorkplaceId();
	const { initializeFromLocalStorage } = useUserDataStore();
	const { securityRoles, currentUserRole } = useUserData();
	const hierarchyNodes = useSettingsStore((s) => s.hierarchyNodes);
	const fetchHierarchyNodes = useSettingsStore((s) => s.fetchHierarchyNodes);
	const organizations = useSettingsStore((s) => s.organizations);
	const fetchOrganizations = useSettingsStore((s) => s.fetchOrganizations);
	const currentUserHierarchyId = getCurrentUserHierarchyId();
	const organizationAdministrator = organizations[0]?.organization_administrator;
	const resolvedUserRole = resolveCurrentUserRole(currentUserRole);

	const canEditListedUser = (user: User): boolean => {
		if (!canEditUser) return false;
		return canEditUserWithSecurityRole(
			resolveUserSecurityRole(user, securityRoles || []),
			resolvedUserRole
		);
	};

	useEffect(() => {
		initializeFromLocalStorage();
		fetchHierarchyNodes();
		fetchOrganizations().catch(() => {
			// Organization record may be unavailable; designated-admin checks then no-op.
		});
	}, [initializeFromLocalStorage, fetchHierarchyNodes, fetchOrganizations]);

	// Build filtered + sorted users list (un-sliced) and derive pagination from it
	const filteredSortedUsers = useMemo(() => {
		if (!users) return [];

		// First filter out system/hidden users
		let filteredUsers = users.filter((user) => !isHiddenFromUserLists(user));

		filteredUsers = filterUsers(filteredUsers, searchTerm);

		filteredUsers = filterUserSettingsUsers(filteredUsers, {
			workplaceId,
			hierarchyNodes,
			showAboveWorkplaceUsers,
			currentUserHierarchyId,
		});

		// Sort users so that:
		// 1) non-separated users come first
		// 2) within that, active users come first
		// 3) then alphabetically by lastName, then firstName
		const sortedUsers = [...filteredUsers].sort((a, b) => {
			// separation: false (0) -> should come before true (1)
			const sepA = a.separation ? 1 : 0;
			const sepB = b.separation ? 1 : 0;
			if (sepA !== sepB) return sepA - sepB;

			// active: true -> should come before false
			const aActive = a.userType?.active ? 1 : 0;
			const bActive = b.userType?.active ? 1 : 0;
			if (aActive !== bActive) return bActive - aActive;

			// finally sort by name
			const lastNameComparison = (a.lastName || '').localeCompare(b.lastName || '', undefined, {
				sensitivity: 'base',
			});
			if (lastNameComparison !== 0) return lastNameComparison;
			return (a.firstName || '').localeCompare(b.firstName || '', undefined, {
				sensitivity: 'base',
			});
		});

		return sortedUsers;
	}, [users, searchTerm, filterUsers, workplaceId, hierarchyNodes, showAboveWorkplaceUsers, currentUserHierarchyId]);

	// Load users once into the shared settings store (skips if already cached)
	useEffect(() => {
		if (viewMode !== 'list') return;
		fetchUsers({
			workplaceId,
			orderBy: 'lastName',
			direction: 'ASC',
			limit: 1000,
			page: 1,
			active: true,
		}).catch((err: any) => {
			console.error('Failed to fetch users:', err);
		});
		// Only fetch when entering the list the first time for this workplace —
		// do not re-fetch merely because viewMode toggled back from edit/create.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchUsers, workplaceId]);

	const handleDeleteClick = (user: User) => {
		if (!canDeleteUser) return;
		if (isDesignatedOrganizationAdministrator(user, organizationAdministrator)) {
			showToast(
				'error',
				'The Organization Administrator configured in Organization Settings cannot be deleted.'
			);
			return;
		}
		console.log(`Delete User ${user.id}`);
		setUserToDelete(user);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (userToDelete) {
			if (isDesignatedOrganizationAdministrator(userToDelete, organizationAdministrator)) {
				showToast(
					'error',
					'The Organization Administrator configured in Organization Settings cannot be deleted.'
				);
				setDeleteDialogOpen(false);
				setUserToDelete(null);
				return;
			}
			try {
				await apiDeleteUser(userToDelete.id);
			} catch (error: any) {
				const message =
					(typeof error === 'string' && error) ||
					error?.message ||
					error?.['hydra:description'] ||
					'Failed to delete user';
				showToast('error', message);
				console.error('Failed to delete user:', error);
			} finally {
				setDeleteDialogOpen(false);
				setUserToDelete(null);
				clearError();
			}
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setUserToDelete(null);
		clearError();
	};

	const handleCreateClick = () => {
		if (!canEditUser) return;
		handleFocusChange?.('user');
		setViewMode('create');
	};

	const handleBackClick = () => {
		handleFocusChange?.(null);
		setViewMode('list');
		setSelectedUser(null);
	};

	const handleShowClick = (user: User) => {
		if (!canViewUser) return;
		console.log(`Show User ${user.id}`);
		handleFocusChange?.('user');
		// Prefer the latest store copy so edits after patch are reflected
		const latest = users.find((u) => u.id === user.id) || user;
		setSelectedUser(latest);
		setViewMode('show');
	};

	const handleEditClick = (user: User) => {
		if (!canEditListedUser(user)) {
			if (canEditUser) {
				showToast(
					'error',
					'You do not have permission to edit users with the Organization Admin role.'
				);
			}
			return;
		}
		console.log(`Edit User ${user.id}`);
		handleFocusChange?.('user');
		const latest = users.find((u) => u.id === user.id) || user;
		setSelectedUser(latest);
		setViewMode('edit');
	};

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* Users Card */}
					<div className={`${styles.workplaceCard} ${styles.userListCard}`}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Users</h2>
							<div className={styles.headerToolbar}>
								<div className={styles.toolbarFilters}>
									<FormControlLabel
										className={styles.filterToggle}
										title="Show Above Workplace Users"
										label={
											<span className={styles.filterToggleLabel}>
												<span className={styles.filterToggleLabelFull}>
													Show Above Workplace Users
												</span>
												<span className={styles.filterToggleLabelShort}>Above Workplace</span>
											</span>
										}
										labelPlacement="start"
										control={
											<Switch
												checked={showAboveWorkplaceUsers}
												onChange={(_, checked) => setShowAboveWorkplaceUsers(checked)}
												size="small"
												inputProps={{ 'aria-label': 'Show Above Workplace Users' }}
												sx={{
													'& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(68, 99, 136)' },
													'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
														backgroundColor: 'rgb(68, 99, 136)',
													},
												}}
											/>
										}
									/>
									<CollapsibleUserSearch value={searchTerm} onChange={setSearchTerm} />
								</div>
								<Button
									className={styles.addButton}
									onClick={handleCreateClick}
									disabled={!canEditUser}
									title={canEditUser ? 'Add User' : 'You do not have permission for this action'}
									style={{
										opacity: canEditUser ? 1 : 0.5,
										cursor: canEditUser ? 'pointer' : 'not-allowed',
									}}
								>
									Add
								</Button>
							</div>
						</div>

					{usersError && <div className={styles.errorMessage}>{usersError}</div>}

					{usersLoading ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									minHeight: '300px',
								}}
							>
								<CircularProgress color="inherit" size={20} />
							</Box>
						) : filteredSortedUsers.length > 0 ? (
							<div
								className={styles.tableContainer}
								style={{
									maxHeight: '500px',
									overflow: 'auto', // Enable both vertical and horizontal scrolling
								}}
							>
								<table
									className={`${styles.workplaceTable} ${styles.userTable}`}
									style={{ tableLayout: 'auto' }}
								>
									<thead>
										<tr>
											<th>First Name</th>
											<th>Last Name</th>
											<th>Hierarchy</th>
											<th style={{ width: '80px' }}>Active</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>View</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
										</tr>
									</thead>
									<tbody>
										{filteredSortedUsers.map((user: User) => (
											<tr key={user?.id}>
												<td style={{ whiteSpace: 'nowrap' }}>{user?.firstName}</td>
												<td style={{ whiteSpace: 'nowrap' }}>{user?.lastName}</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													{cleanHierarchyName(user?.hierarchy?.name)}
												</td>
												<td style={{ textAlign: 'center' }}>
													{user?.separation ? 'Separated' : user?.userType?.active ? 'Yes' : 'No'}
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleShowClick(user)}
															title={
																canViewUser
																	? 'View User'
																	: 'You do not have permission for this action'
															}
															disabled={!canViewUser}
															style={{
																opacity: canViewUser ? 1 : 0.5,
																cursor: canViewUser ? 'pointer' : 'not-allowed',
															}}
														>
															<Visibility className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														{(() => {
															const editEnabled = canEditListedUser(user);
															const blockedOrgAdmin =
																canEditUser &&
																!editEnabled;
															return (
																<button
																	className={styles.iconButton}
																	onClick={() => handleEditClick(user)}
																	title={
																		blockedOrgAdmin
																			? 'You do not have permission to edit Organization Admin users'
																			: canEditUser
																				? 'Edit User'
																				: 'You do not have permission for this action'
																	}
																	disabled={!editEnabled}
																	style={{
																		opacity: !editEnabled ? 0.5 : 1,
																		cursor: !editEnabled ? 'not-allowed' : 'pointer',
																	}}
																>
																	<Edit className={styles.editIcon} />
																</button>
															);
														})()}
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														{(() => {
															const isProtectedAdmin = isDesignatedOrganizationAdministrator(
																user,
																organizationAdministrator
															);
															const deleteEnabled = canDeleteUser && !isProtectedAdmin;
															return (
																<button
																	className={styles.iconButton}
																	onClick={() => handleDeleteClick(user)}
																	title={
																		isProtectedAdmin
																			? 'The Organization Administrator cannot be deleted'
																			: canDeleteUser
																				? 'Delete User'
																				: 'You do not have permission for this action'
																	}
																	disabled={!deleteEnabled}
																	style={{
																		opacity: !deleteEnabled ? 0.5 : 1,
																		cursor: !deleteEnabled ? 'not-allowed' : 'pointer',
																	}}
																>
																	<Delete className={styles.deleteIcon} />
																</button>
															);
														})()}
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : !usersError ? (
							<div className={styles.noDataMessage}>
								{searchTerm.trim() ? 'No users found matching your search.' : 'No users found.'}
							</div>
						) : null}

						{/* Delete Confirmation Dialog */}
						<Dialog
							open={deleteDialogOpen}
							onClose={handleDeleteCancel}
							aria-labelledby="delete-dialog-title"
							aria-describedby="delete-dialog-description"
						>
							<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
							<DialogContent>
								<DialogContentText id="delete-dialog-description">
									Are you sure you want to delete this user?
									{userToDelete &&
										` ${userToDelete.firstName} ${userToDelete.lastName} will be permanently removed.`}
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								<Button className={styles.mabelButton} onClick={handleDeleteCancel}>
									Cancel
								</Button>
								<Button className={styles.mabelButton} onClick={handleDeleteConfirm}>
									Delete
								</Button>
							</DialogActions>
						</Dialog>
					</div>
				</>
			);
		case 'create':
			return (
				<div className={styles.createWorkplaceContainer}>
					<CreateUserSettings
						backButtonClick={handleBackClick}
					/>
				</div>
			);
		case 'edit':
			{
				const latestUser =
					(selectedUser &&
						users.find((u) => Number(u.id) === Number(selectedUser.id))) ||
					selectedUser;
				return (
					<div className={styles.showWorkplaceContainer}>
						{latestUser && (
							<EditUserSettings user={latestUser} handleBackClick={handleBackClick} />
						)}
					</div>
				);
			}
		default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<h2 className={styles.formTitle}>User Details</h2>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedUser && <ShowUserSetting user={selectedUser} />}
				</div>
			);
	}
}

export default UserSettings;
